import { z } from 'zod';

export interface PartyRoute {
  id: string;
  entityId: string;
  label?: string;
  /** Editor-only identity for empty draft rows. Not persisted. */
  rowKey?: string;
}

const unknownListSchema = z.array(z.unknown());
const unknownRecordSchema = z.record(z.string(), z.unknown());
const stringSchema = z.string();
const nonemptyStringSchema = z.string().min(1);

function readString(value: unknown): string | undefined {
  const parsed = stringSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

export function parsePartyRoutes(
  raw: unknown,
  entityIdKey: string,
): PartyRoute[] {
  const list = unknownListSchema.safeParse(raw);
  if (!list.success) return [];
  return list.data.map((entry) => parsePartyRouteEntry(entry, entityIdKey));
}

function parsePartyRouteEntry(entry: unknown, entityIdKey: string): PartyRoute {
  const parsed = unknownRecordSchema.safeParse(entry);
  if (!parsed.success) return { id: '', entityId: '' };
  const rec = parsed.data;
  const rowKey = readString(rec.rowKey);
  return {
    id: readString(rec.id) ?? '',
    entityId: readString(rec[entityIdKey]) ?? '',
    label: readString(rec.label),
    ...(rowKey && rowKey.length > 0 ? { rowKey } : {}),
  };
}

export function serializePartyRoutes(
  routes: PartyRoute[],
  entityIdKey: string,
): Array<Record<string, unknown>> {
  return routes.map((route) => ({
    id: route.id,
    [entityIdKey]: route.entityId,
    ...(route.label != null && route.label.length > 0
      ? { label: route.label }
      : {}),
    ...(route.rowKey != null && route.rowKey.length > 0
      ? { rowKey: route.rowKey }
      : {}),
  }));
}

export function countEntityIds(routes: PartyRoute[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const route of routes) {
    if (!route.entityId) continue;
    counts.set(route.entityId, (counts.get(route.entityId) ?? 0) + 1);
  }
  return counts;
}

export function isDuplicateEntityId(
  entityId: string,
  counts: Map<string, number>,
): boolean {
  return entityId.length > 0 && (counts.get(entityId) ?? 0) > 1;
}

export function siblingIdSets(routes: PartyRoute[]): Set<string>[] {
  return routes.map((_, index) => {
    const ids = new Set<string>();
    for (const [j, route] of routes.entries()) {
      if (j === index || route.id.length === 0) continue;
      ids.add(route.id);
    }
    return ids;
  });
}

export function parseUuidList(value: unknown): string[] {
  const parsed = unknownListSchema.safeParse(value);
  if (!parsed.success) return [];
  const ids: string[] = [];
  for (const id of parsed.data) {
    const asString = nonemptyStringSchema.safeParse(id);
    if (asString.success) ids.push(asString.data);
  }
  return ids;
}

/** Stable React key. Empty drafts fall back to index so siblings stay distinct. */
export function partyRouteRowKey(route: PartyRoute, index: number): string {
  if (route.rowKey && route.rowKey.length > 0) return route.rowKey;
  if (route.id.length > 0) return route.id;
  if (route.entityId.length > 0) return `entity:${route.entityId}`;
  return `empty-route-${index}`;
}
